import { z } from 'zod';

import {
  AppDetectionSemanticFieldsSchema,
  MeetingProviderSchema,
  StartMeetingInputSchema,
  StopNoteTakingReasonSchema,
  UpcomingMeetingPayloadSchema,
} from './meetings.types';
import { SOUND_IDS } from './sounds.types';
import { WindowLabels } from './window.types';

export {
  UpcomingMeetingPayloadSchema,
  type MeetingProvider,
} from './meetings.types';

export enum LbNotificationCommands {
  QueueNotification = 'lb-queue-notification',
  SendNativeNotification = 'lb-send-native-notification',
  ClearAll = 'lb-clear-all-notifications',
  QueueCount = 'lb-queue-count',
  HasNotification = 'lb-has-notification',
  RemoveQueuedById = 'lb-remove-queued-by-id',
  DismissById = 'lb-dismiss-by-id',
  GetVolume = 'lb-get-volume',
  SetVolume = 'lb-set-volume',
  GetEnabled = 'lb-get-notifications-enabled',
  SetEnabled = 'lb-set-notifications-enabled',
  SetSoundChoice = 'lb-set-notification-sound-choice',
  PreviewSoundChoice = 'lb-preview-notification-sound-choice',
  RequestDismissNotification = 'lb-request-dismiss-notification',
  NotificationAction = 'lb-notification-action',
  InteractionChanged = 'lb-notification-interaction-changed',
}

export enum LbNotificationEvents {
  Ready = 'lb-notification-ready',
  WillNotify = 'lb-notification-will-notify',
  DidPresent = 'lb-notification-did-present',
  DidNotify = 'lb-notification-did-notify',
  QueueChanged = 'lb-queue-changed',
}

export const DEFAULT_NOTIFICATION_VOLUME = 0.25;

export const NotificationIdSchema = z.string().min(1);

export const MeetingDetectedPayloadSchema =
  AppDetectionSemanticFieldsSchema.extend({
    appName: z.string(),
    autoStartOnDetection: z.boolean().optional(),
    autoStartAttemptId: z.string().optional(),
    meetingLimitReached: z.boolean().optional(),
    calendarEventTitle: z.string().optional(),
    websiteHost: z.string().optional(),
    isBrowser: z.boolean().optional(),
  });

export const MeetingPresenceLostPayloadSchema = z.object({
  meetingId: z.string(),
  /** Why the recording stopped; without it the view says the meeting app is gone */
  message: z.string().optional(),
  stopReason: StopNoteTakingReasonSchema.extract([
    'inactivity_timeout',
    'meeting_presence_lost',
  ]).optional(),
});

export const MeetingNewEventStartedPayloadSchema = z.object({
  eventId: z.string(),
  eventTitle: z.string(),
  provider: MeetingProviderSchema.optional(),
  precedingMeetingId: z.string(),
});

export const MeetingSegmentSwitchPayloadSchema = z.object({
  precedingMeetingId: z.string(),
  appName: z.string(),
  appBundleId: z.string().optional(),
  provider: MeetingProviderSchema.optional(),
  meetingKey: z.string(),
  eventId: z.string().optional(),
  eventTitle: z.string().optional(),
  autoSwitchOnDetection: z.boolean().optional(),
});

export const MeetingStartFailedPayloadSchema = z.object({
  meetingLabel: z.string(),
  provider: MeetingProviderSchema.optional(),
  retryPayload: StartMeetingInputSchema,
});

export const MeetingSelectedInputSilentPayloadSchema = z.object({});
export const MeetingInputDeviceConnectedPayloadSchema = z.object({
  meetingId: z.string(),
  deviceId: z.string(),
  deviceName: z.string(),
});

export const NotificationActionSchema = z.object({
  id: z.string(),
  label: z.string(),
  variant: z.enum(['default', 'destructive']).default('default'),
});

export const NotificationRouteSchema = z.object({
  to: z.string(),
  params: z.record(z.string(), z.string()).optional(),
  search: z.record(z.string(), z.unknown()).optional(),
  hash: z.string().optional(),
});

export const BasicNotificationPayloadSchema = z.object({
  title: z.string(),
  message: z.string(),
  contentKey: z.string().optional(),
  cta: z.string().optional(),
  actions: z.array(NotificationActionSchema).optional(),
  route: NotificationRouteSchema.optional(),
  autoDismiss: z.union([z.boolean(), z.number().min(0)]).optional(),
});

export const RoutineReportPayloadSchema = z.object({
  title: z.string(),
  subtitle: z.string().optional(),
  previewImage: z.string(),
  route: NotificationRouteSchema,
  autoDismiss: z.union([z.boolean(), z.number().min(0)]).optional(),
});

export const NotificationEnvelopeSchema = z.object({
  id: NotificationIdSchema,
  sound: z.enum([...SOUND_IDS, 'none'] as const),
  expiresAt: z.coerce.date().optional(),
  transient: z.boolean().optional(),
});

export const PanelNotificationEnvelopeSchema =
  NotificationEnvelopeSchema.extend({
    delivery: z
      .enum(['queued', 'expedited'])
      .optional()
      .describe(
        'expedited: takes the panel from whatever is on screen, unless the user is interacting with a different notification, in which case it waits at the front of the queue for a bounded time'
      ),
  });

export const NativeNotificationSchema = NotificationEnvelopeSchema.extend({
  title: z.string(),
  message: z.string(),
  route: NotificationRouteSchema.optional(),
  // Window the click opens; defaults to main. Hummingbird opens the thread in
  // `route.params.chatId`.
  target: z.enum([WindowLabels.main, WindowLabels.hummingbird]).optional(),
  actions: z.array(NotificationActionSchema).optional(),
  autoDismiss: z.boolean().optional(),
  // What the server calls the push, such as `hive_mind`, for the analytics event of its click
  notificationType: z.string().optional(),
});

export const NotificationActionEventSchema = z.object({
  notificationId: NotificationIdSchema,
  actionId: z.string(),
});

export const NotificationInteractionEventSchema = z.object({
  notificationId: NotificationIdSchema,
  interacting: z.boolean(),
});

export const LbNotificationSchema = z.discriminatedUnion('view', [
  z
    .object({
      view: z.literal('basic'),
      payload: BasicNotificationPayloadSchema,
    })
    .merge(PanelNotificationEnvelopeSchema),
  z
    .object({
      view: z.literal('upcoming-meeting'),
      payload: UpcomingMeetingPayloadSchema,
    })
    .merge(PanelNotificationEnvelopeSchema),
  z
    .object({
      view: z.literal('meeting-detected'),
      payload: MeetingDetectedPayloadSchema,
    })
    .merge(PanelNotificationEnvelopeSchema),
  z
    .object({
      view: z.literal('meeting-presence-lost'),
      payload: MeetingPresenceLostPayloadSchema,
    })
    .merge(PanelNotificationEnvelopeSchema),
  z
    .object({
      view: z.literal('meeting-new-event-started'),
      payload: MeetingNewEventStartedPayloadSchema,
    })
    .merge(PanelNotificationEnvelopeSchema),
  z
    .object({
      view: z.literal('meeting-segment-switch'),
      payload: MeetingSegmentSwitchPayloadSchema,
    })
    .merge(PanelNotificationEnvelopeSchema),
  z
    .object({
      view: z.literal('meeting-start-failed'),
      payload: MeetingStartFailedPayloadSchema,
    })
    .merge(PanelNotificationEnvelopeSchema),
  z
    .object({
      view: z.literal('meeting-selected-input-silent'),
      payload: MeetingSelectedInputSilentPayloadSchema,
    })
    .merge(PanelNotificationEnvelopeSchema),
  z
    .object({
      view: z.literal('meeting-input-device-connected'),
      payload: MeetingInputDeviceConnectedPayloadSchema,
    })
    .merge(PanelNotificationEnvelopeSchema),
  z
    .object({
      view: z.literal('routine-report'),
      payload: RoutineReportPayloadSchema,
    })
    .merge(PanelNotificationEnvelopeSchema),
]);

export type NotificationRoute = z.infer<typeof NotificationRouteSchema>;
export type NativeNotification = z.infer<typeof NativeNotificationSchema>;
export type PanelNotification = z.infer<typeof LbNotificationSchema>;
export type LbNotification = PanelNotification;
export type NotificationActionEvent = z.infer<
  typeof NotificationActionEventSchema
>;
export type NotificationInteractionEvent = z.infer<
  typeof NotificationInteractionEventSchema
>;
export type LbNotificationView = PanelNotification['view'];

export const LbNotificationExpiry = {
  alert: (from = new Date()) => new Date(from.getTime() + 2 * 60 * 1000),
};
